import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { dismissBulkOrderJobProgress } from "../../redux/reducers/bulkOrderJobReducer";
import {
  getBulkOrderJobPercent,
  isBulkOrderEnqueuePhase,
  isBulkOrderJobTerminal,
} from "../../types/bulkOrderJobProgress";

const AUTO_DISMISS_MS = 5000;

const BulkOrderJobProgressBar = () => {
  const dispatch = useDispatch();
  const { activeJob, visible } = useSelector(
    (state: RootState) => state.bulkOrderJob,
  );

  const percent = getBulkOrderJobPercent(activeJob);
  const isTerminal = isBulkOrderJobTerminal(activeJob);
  const isEnqueuePhase = isBulkOrderEnqueuePhase(activeJob);
  const isFailed = activeJob?.status === "failed";
  const isCompleted = activeJob?.status === "completed" || percent >= 100;

  useEffect(() => {
    if (!visible || !isTerminal) return;

    const timer = window.setTimeout(() => {
      dispatch(dismissBulkOrderJobProgress());
    }, AUTO_DISMISS_MS);

    return () => window.clearTimeout(timer);
  }, [dispatch, visible, isTerminal, activeJob?.jobId, activeJob?.status]);

  if (!visible || !activeJob) return null;

  const statusLabel = isFailed
    ? "Bulk order failed"
    : isCompleted
      ? "Bulk order complete"
      : isEnqueuePhase
        ? `Enqueueing ${activeJob.orderType} orders`
        : `Processing ${activeJob.orderType} orders`;

  const detailText = isEnqueuePhase
    ? `${activeJob.enqueuedCount} / ${activeJob.totalCount} orders enqueued`
    : `${activeJob.processedCount} / ${activeJob.totalCount} orders processed`;

  const barColor = isFailed
    ? "bg-red-500"
    : isCompleted
      ? "bg-emerald-500"
      : "bg-[#004EFF]";

  return (
    <div className="fixed top-[68px] right-4 z-[60] w-[min(300px,calc(100vw-2rem))]">
      <div className="rounded-xl border border-[#E5E7EB] bg-white p-3 shadow-[0_8px_24px_rgba(0,0,0,0.08)]">
        <div className="mb-2 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold font-Open text-[#1C1C1C]">
              {statusLabel}
            </p>
            <p className="mt-0.5 truncate text-xs font-Open text-[#6B7280]">
              {detailText}
            </p>
          </div>
          <button
            type="button"
            aria-label="Dismiss bulk order progress"
            className="shrink-0 rounded-md px-1.5 py-0.5 text-xs font-Open text-[#9CA3AF] hover:bg-gray-100 hover:text-[#4B5563]"
            onClick={() => dispatch(dismissBulkOrderJobProgress())}
          >
            ✕
          </button>
        </div>

        <div className="flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#EEF2FF]">
            <div
              className={`h-full rounded-full transition-all duration-300 ease-out ${barColor}`}
              style={{ width: `${percent}%` }}
            />
          </div>
          <span className="w-9 shrink-0 text-right text-xs font-semibold font-Open text-[#004EFF]">
            {percent}%
          </span>
        </div>

        {(activeJob.failedCount > 0 || activeJob.enqueueFailedCount > 0) && (
          <div className="mt-2 flex gap-3 text-[11px] font-Open text-[#6B7280]">
            {activeJob.enqueueFailedCount > 0 && (
              <span className="text-red-500">
                {activeJob.enqueueFailedCount} enqueue failed
              </span>
            )}
            {activeJob.failedCount > 0 && (
              <span className="text-red-500">
                {activeJob.failedCount} processing failed
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkOrderJobProgressBar;
